import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText, Download, Trash2 } from 'lucide-react';
import { DashboardLayout } from '../../components/layout';
import { facultyExamApi } from '../../api/examApi';
import { toast } from 'sonner';
import { parseApiError } from '../../lib/apiErrors';

export default function FacultyExamsPage() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      const response = await facultyExamApi.getMyExams();
      setExams(response.data);
    } catch (err) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (examId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';
      await facultyExamApi.updateExam(examId, { is_published: newStatus });
      toast.success(newStatus === 'published' ? 'Exam published!' : 'Exam unpublished');
      loadExams();
    } catch (err) {
      toast.error('Failed to update exam');
    }
  };

  const handleDownloadAll = async (examId, event) => {
    event.stopPropagation();
    try {
      const response = await facultyExamApi.downloadAllSubmissions(examId);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'exam_submissions.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(parseApiError(err, 'No submitted papers available yet').message);
    }
  };

  const handleDeleteExam = async (examId, event) => {
    event.stopPropagation();
    const confirmed = window.confirm('Delete this exam and all its submissions? This cannot be undone.');
    if (!confirmed) return;
    try {
      await facultyExamApi.deleteExam(examId);
      toast.success('Exam deleted');
      loadExams();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete exam').message);
    }
  };

  return (
    <DashboardLayout title="Exam Management">
      <div className="animate-fade-in space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              My Exams
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Create and manage exams for students
            </p>
          </div>
          <Link
            to="/faculty/exams/create"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2.5 transition-colors duration-200 font-medium"
            data-testid="create-exam-btn"
          >
            <Plus size={20} />
            Create Exam
          </Link>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            ))}
          </div>
        ) : exams.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400 mb-4">No exams created yet</p>
            <Link
              to="/faculty/exams/create"
              className="inline-flex items-center gap-2 text-primary dark:text-accent-cool hover:underline"
            >
              <Plus size={18} />
              Create your first exam
            </Link>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Exam</th>
                  <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Type</th>
                  <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Duration</th>
                  <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Marks</th>
                  <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Status</th>
                  <th className="text-right font-medium text-slate-500 dark:text-slate-400 p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {exams.map(exam => (
                  <tr 
                    key={exam.id}
                    className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/50 dark:border-slate-800 dark:hover:bg-slate-800/50"
                    data-testid={`exam-row-${exam.id}`}
                    onClick={() => navigate(`/faculty/exams/${exam.id}`)}
                  >
                    <td className="p-4">
                      <p className="font-medium text-slate-900 dark:text-white">{exam.title}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{exam.subject}</p>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium text-slate-700 dark:text-slate-300 capitalize">
                        {exam.exam_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-4 text-slate-700 dark:text-slate-300">
                      {exam.duration_minutes} min
                    </td>
                    <td className="p-4 text-slate-700 dark:text-slate-300">
                      {exam.total_marks}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        exam.is_published === 'published'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {exam.is_published}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(event) => handleDownloadAll(exam.id, event)}
                          className="rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                          title="Download all papers"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={(event) => handleDeleteExam(exam.id, event)}
                          className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                          title="Delete exam"
                        >
                          <Trash2 size={18} />
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handlePublish(exam.id, exam.is_published);
                          }}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            exam.is_published === 'published'
                              ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400'
                          }`}
                        >
                          {exam.is_published === 'published' ? 'Unpublish' : 'Publish'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
